import { useState, useRef, useEffect, useCallback, memo } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { SVGIcon } from '../SVGIcon';
import { BadgeStatusType } from '../Badge/BadgeTypes';
import { Badge } from '../Badge/Badge';
import { SliderProgress } from '../SliderProgress';
import type { TireData, TirePosition } from './TiresControl.types';
import { TireIconWithStatus } from './TireIconWithStatus';

import 'swiper/css';

import leftCar from './left.png';
import rightCar from './right.png';

type SideType = 'left' | 'right';

interface TireVisualizationMobileProps {
  isSelected: boolean;
  className?: string;
}

const TireVisualizationMobile = memo(({
  isSelected,
  className,
}: TireVisualizationMobileProps) => {
  return (
    <div className={clsx('flex flex-col items-center pointer-events-none w-[12%]', className)}>
      <div className="relative w-full aspect-square flex-shrink-0">
        <svg className="w-full h-full" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="25" cy="25" r="24" stroke="#DADDEB" strokeWidth="2" strokeDasharray="2 2" />
        </svg>

        <div
          className={clsx(
            'absolute top-1/2 left-1/2 rounded-full transition-all duration-300 ease-out -translate-x-1/2 -translate-y-1/2 w-full h-full',
            isSelected && 'bg-[#0064FF]/30 border-2 border-primary',
          )}
        />
      </div>
      {/* Pionowa linia ze znacznikiem */}
      <div className="flex z-20 flex-col items-center w-full flex-1">
        <div className={clsx('flex-1 transition-all duration-300 ease-out w-[2px]', isSelected ? 'bg-primary' : 'bg-gray-400')} />
        <div className={clsx('flex-shrink-0 w-1.5 h-1.5 rounded-full transition-all duration-300 ease-out -mb-[3px]', isSelected ? 'bg-primary' : 'bg-gray-400')} />
      </div>
    </div>
  );
});

interface TireCardMobileProps {
  tire: TireData;
  isSelected: boolean;
  onClick: () => void;
}

const TireCardMobile = memo(({ tire, isSelected, onClick }: TireCardMobileProps) => {
  return (
    <div
      className={clsx(
        'flex flex-col w-full h-full transition-all duration-300 ease-out cursor-pointer',
        isSelected ? 'border-2 border-primary bg-[#FAFCFF]' : 'border border-grayBorder bg-white',
      )}
      onClick={onClick}
    >
      {/* Header sekcja - Opona */}
      <div className="px-3 pt-3">
        {/* Górny wiersz: ikona + nazwa + badge */}
        <div className="flex justify-between items-start mb-5">
          <div className="flex gap-2 items-center">
            <TireIconWithStatus tire={tire} type="tire" />
            <h3 className="text-[20px] font-bold font-archivo leading-4 tracking-[0.2px] text-text-primary">
              Opona
            </h3>
          </div>
          {tire.replacementStatus && (
            <div className="flex flex-col items-end">
              <span className="font-inter text-xs leading-4 text-text-primary mb-0.5">
                Do wymiany:
              </span>
              <Badge
                variant={
                  tire.replacementStatus === 'Tak'
                    ? BadgeStatusType.Error
                    : tire.replacementStatus === 'Nie'
                      ? BadgeStatusType.Success
                      : BadgeStatusType.Warning
                }
                size="sm"
                version="v1"
                classNames="ml-auto"
                badgeText={tire.replacementStatus}
              />
            </div>
          )}
        </div>

        {/* Marka, model i rozmiar */}
        <div className="flex justify-between items-start mb-5">
          <div>
            <h4 className="text-[17px] font-semibold font-archivo leading-[18px] text-text-primary">
              {tire.brand}
            </h4>
            <p className="text-[13px] font-normal font-archivo leading-[15px] tracking-[0.3px] text-text-muted">
              {tire.model}
            </p>
          </div>
          <Badge variant={BadgeStatusType.Neutral} size="sm" version="v2" badgeText={tire.size} classNames='text-grayText text-[11px] font-medium uppercase tracking-[0.4px]' />
        </div>

        {/* Szczegóły opony */}
        <div className="space-y-[14px]">
          <div className="flex gap-3 items-center">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
              <SVGIcon name="location-pin-map" size={20} fill="grayStale" />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-normal font-inter leading-[14px] text-grayText">Rodzaj opony</span>
              <span className="text-[14px] font-semibold font-inter leading-4 text-text-primary">{tire.type}</span>
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
              <SVGIcon name="calendar-date" size={20} fill="grayStale" />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-normal font-inter leading-[14px] text-grayText">Kod daty produkcji</span>
              <span className="text-[14px] font-semibold font-inter leading-4 text-text-primary">{tire.productionDate}</span>
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
              <SVGIcon name="media-play" size={20} fill="grayStale" />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-normal font-inter leading-[14px] text-grayText">Indeks prędkości</span>
              <span className="text-[14px] font-semibold font-inter leading-4 text-text-primary">{tire.speedIndex}</span>
            </div>
          </div>

          {/* Bieżnik */}
          <div >
            <div className="flex gap-3 items-center">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
                <SVGIcon name="server-stack" size={20} fill="grayStale" />
              </div>
              <span className="text-[14px] font-semibold font-inter leading-4 text-text-primary">Bieżnik [mm]:</span>
            </div>

            <div className="flex gap-4 mt-2">
              <div>
                <span className="text-[13px] font-normal font-inter text-grayText">Str. wewn: </span>
                <span className="text-[14px] font-semibold font-inter text-text-primary">{tire.treadDepth.inner}</span>
              </div>
              <div>
                <span className="text-[13px] font-normal font-inter text-grayText">Środek: </span>
                <span className="text-[14px] font-semibold font-inter text-text-primary">{tire.treadDepth.center}</span>
              </div>
              <div>
                <span className="text-[13px] font-normal font-inter text-grayText">Str. zewn: </span>
                <span className="text-[14px] font-semibold font-inter text-text-primary">{tire.treadDepth.outer}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Felga */}
      <div className="px-3 py-5 mt-5 border-t">
        <div className="flex gap-2 items-center">
          <TireIconWithStatus tire={tire} type="rim" />
          <h3 className="text-[20px] font-bold font-archivo leading-4 tracking-[0.2px] text-text-primary">Felga</h3>
          <div className="flex flex-col gap-1 ml-auto">
            <div>
              <span className="text-[13px] font-normal font-inter text-grayText">Rodzaj: </span>
              <span className="text-[14px] font-semibold font-inter text-text-primary">{tire.rimType}</span>
            </div>
            <div>
              <span className="text-[13px] font-normal font-inter text-grayText">Stan: </span>
              <span className="text-[14px] font-semibold font-inter text-text-primary">{tire.rimStatus}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export interface TiresControlMobileSideProps {
  side: SideType;
  frontTire: TireData | undefined;
  rearTire: TireData | undefined;
  selectedPosition: TirePosition | null;
  onTireSelect: (position: TirePosition) => void;
}

export const TiresControlMobileSide = ({
  side,
  frontTire,
  rearTire,
  selectedPosition,
  onTireSelect,
}: TiresControlMobileSideProps) => {
  const isLeftSide = side === 'left';
  const carImage = isLeftSide ? leftCar : rightCar;
  const sideLabel = isLeftSide ? 'Strona lewa' : 'Strona prawa';

  const frontPosition: TirePosition = isLeftSide ? 'front-left' : 'front-right';
  const rearPosition: TirePosition = isLeftSide ? 'rear-left' : 'rear-right';

  // Na prawej stronie obraz jest lustrzany — lewa część to tył auta, prawa to przód.
  // Pierwszy slajd (lewa część obrazu) powinien więc pokazywać tylną oponę.
  const firstTire = isLeftSide ? frontTire : rearTire;
  const secondTire = isLeftSide ? rearTire : frontTire;
  const firstPosition = isLeftSide ? frontPosition : rearPosition;
  const secondPosition = isLeftSide ? rearPosition : frontPosition;

  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [carTranslateX, setCarTranslateX] = useState(0);
  const [sliderProgress, setSliderProgress] = useState(0);

  const isFirstSelected = selectedPosition === firstPosition;
  const isSecondSelected = selectedPosition === secondPosition;

  // Która opona jest aktywna na tej stronie (bazując na swiper index)
  const activeOnThisSide = activeIndex === 0 ? 'first' : 'second';

  const handleTireClick = useCallback((type: 'first' | 'second') => {
    const index = type === 'first' ? 0 : 1;
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
    setActiveIndex(index);
    const position = type === 'first' ? firstPosition : secondPosition;
    onTireSelect(position);
  }, [firstPosition, secondPosition, onTireSelect]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex);
    const position = swiper.activeIndex === 0 ? firstPosition : secondPosition;
    onTireSelect(position);
  }, [firstPosition, secondPosition, onTireSelect]);

  const handleProgress = useCallback((_swiper: SwiperType, progress: number) => {
    // Przesuwanie obrazka samochodu w tym samym kierunku co swipe
    // progress: 0 = pierwsza opona (lewy box = lewa część obrazka)
    // progress: 1 = druga opona (prawy box = prawa część obrazka)
    // translateX: +25% gdy progress=0 (pokazuje lewą część), -25% gdy progress=1 (pokazuje prawą część)
    const translateValue = (0.5 - progress) * 50; // od +25 do -25
    setCarTranslateX(translateValue);
    setSliderProgress(progress);
  }, []);

  // Synchronizacja swipera z selectedPosition
  useEffect(() => {
    if (swiperRef.current) {
      if (isFirstSelected && swiperRef.current.activeIndex !== 0) {
        swiperRef.current.slideTo(0);
      } else if (isSecondSelected && swiperRef.current.activeIndex !== 1) {
        swiperRef.current.slideTo(1);
      }
    }
  }, [isFirstSelected, isSecondSelected]);

  const tiresCount = [firstTire, secondTire].filter(Boolean).length;

  return (
    <>
      <div className="overflow-hidden bg-white border border-b-0 rounded-t-card border-grayBorder">
        {/* Nagłówek strony */}
        <div className="px-4 py-3">
          <h3 className="text-xs font-normal font-inter leading-[14px] text-text-primary">{sideLabel}</h3>
        </div>

        {/* Obrazek samochodu z klikanymi oponami - przesuwany */}
        <div className="overflow-hidden relative">
          <div
            className="relative transition-transform duration-300 ease-out"
            style={{
              transform: `translateX(${carTranslateX}%)`,
              width: '150%',
              marginLeft: '-25%'
            }}
          >
            <img src={carImage} className="w-full" alt={sideLabel} />

            {/* Punkty opon na obrazku */}
            {/* Lewa strona: przód po lewej, tył po prawej */}
            {/* Prawa strona: przód po prawej, tył po lewej */}
            {firstTire && (
              <TireVisualizationMobile
                className={clsx(
                  'absolute bottom-0',
                  isLeftSide ? 'top-[42%] left-[15.2%]' : 'top-[42%] left-[16.2%]',
                )}
                isSelected={activeOnThisSide === 'first'}
              />
            )}

            {secondTire && (
              <TireVisualizationMobile
                className={clsx(
                  'absolute bottom-0',
                  isLeftSide ? 'top-[42%] right-[16.2%]' : 'top-[42%] right-[15.2%]',
                )}
                isSelected={activeOnThisSide === 'second'}
              />
            )}
          </div>
        </div>

        {/* Swiper z kartami opon */}
        <div className="">
          <>
            <Swiper
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              slidesPerView={1.15}
              spaceBetween={0}
              onSlideChange={handleSlideChange}
              onProgress={handleProgress}
              className="!overflow-visible"
            >
              {firstTire && (
                <SwiperSlide className="!h-auto">
                  <TireCardMobile
                    tire={firstTire}
                    isSelected={activeOnThisSide === 'first'}
                    onClick={() => handleTireClick('first')}
                  />
                </SwiperSlide>
              )}
              {secondTire && (
                <SwiperSlide className="!h-auto">
                  <TireCardMobile
                    tire={secondTire}
                    isSelected={activeOnThisSide === 'second'}
                    onClick={() => handleTireClick('second')}
                  />
                </SwiperSlide>
              )}
            </Swiper>
          </>
        </div>
      </div>
      <div className="px-4 mt-3">
        <SliderProgress
          totalSlides={tiresCount}
          progress={sliderProgress}
          isProgressValue={true}
        />
      </div>
    </>
  );
};
